import React, { useState, useEffect, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trophy, Loader2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import DatePicker from "@/components/DatePicker";
import { api, toast } from "../../App";

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * ManageAwardsSheet — Phase 2 of the partial-award workflow.
 *
 * Props:
 *   open          : boolean
 *   onOpenChange  : (bool) => void
 *   estimationId  : string
 *   onSaved       : () => void  // fired after each successful mutation so the parent can refresh
 */
export const ManageAwardsSheet = ({ open, onOpenChange, estimationId, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [estimation, setEstimation] = useState(null);

  const load = async () => {
    if (!estimationId) return;
    setLoading(true);
    try {
      const res = await api.get(`/estimations/${estimationId}`);
      setEstimation(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load estimation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open, estimationId]);

  // Award-date prompt: { lineItemId } for one item, { all: true } for "Award All"
  const [datePrompt, setDatePrompt] = useState(null);
  const [awardDate, setAwardDate] = useState(localToday());
  const askDate = (target) => { setAwardDate(target.current || localToday()); setDatePrompt(target); };
  const confirmDate = async () => {
    if (!awardDate) return toast.error("Award date is required");
    if (awardDate > localToday()) return toast.error("Award date can't be in the future");
    const target = datePrompt;
    setDatePrompt(null);
    if (target.all) await awardAll(awardDate);
    else await toggleLine(target.lineItemId, "awarded", awardDate);
  };

  const toggleLine = async (lineItemId, nextStatus, awardedDate) => {
    setSaving(true);
    try {
      const res = await api.patch(
        `/estimations/${estimationId}/line-items/${lineItemId}/award`,
        { award_status: nextStatus, ...(awardedDate ? { awarded_date: awardedDate } : {}) }
      );
      setEstimation(res.data);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update line item");
    } finally {
      setSaving(false);
    }
  };

  const awardAll = async (awardedDate) => {
    setSaving(true);
    try {
      const res = await api.patch(`/estimations/${estimationId}/line-items/award-all`, { awarded_date: awardedDate });
      setEstimation(res.data);
      toast.success("All pending line items awarded.");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to award all");
    } finally {
      setSaving(false);
    }
  };

  const lineItems = estimation?.line_items || [];
  const stats = useMemo(() => {
    let awardedCount = 0;
    let pendingCount = 0;
    let awardedHours = 0;
    let awardedValue = 0;
    let pendingHours = 0;
    let pendingValue = 0;
    lineItems.forEach((li) => {
      const isAwarded = (li.award_status || "pending").toLowerCase() === "awarded";
      const h = parseFloat(li.estimated_detailing_hours || 0) || 0;
      const v = parseFloat(li.subtotal || 0) || 0;
      if (isAwarded) {
        awardedCount++;
        awardedHours += h;
        awardedValue += v;
      } else {
        pendingCount++;
        pendingHours += h;
        pendingValue += v;
      }
    });
    return { awardedCount, pendingCount, awardedHours, awardedValue, pendingHours, pendingValue };
  }, [lineItems]);

  const fmtCurrency = (v) => {
    const n = Number(v || 0);
    const code = estimation?.currency || "USD";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: code,
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return `${code} ${n.toFixed(0)}`;
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
    } catch {
      return iso;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[760px] overflow-y-auto" data-testid="manage-awards-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-600" />
            Manage Awards
            {estimation && (
              <span className="text-sm text-gray-500 font-normal">
                {estimation.estimation_number} · v{estimation.version}
              </span>
            )}
          </SheetTitle>
          <SheetDescription>
            Mark the line items that have been awarded. Any award change on an estimation linked to a
            project shows as an "Award update pending" on that project.
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span data-testid="award-stats-awarded">
                  Awarded: <b>{stats.awardedCount}</b> ({stats.awardedHours}h · {fmtCurrency(stats.awardedValue)})
                </span>
                {" · "}
                <span data-testid="award-stats-pending">
                  Pending: <b>{stats.pendingCount}</b> ({stats.pendingHours}h · {fmtCurrency(stats.pendingValue)})
                </span>
              </div>
              <Button
                size="sm"
                variant="default"
                onClick={() => askDate({ all: true })}
                disabled={saving || stats.pendingCount === 0}
                data-testid="award-all-btn"
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                Select All Pending → Award
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Awarded</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">Subtotal</TableHead>
                  <TableHead>Awarded Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lineItems.map((li, idx) => {
                  const isAwarded = (li.award_status || "pending").toLowerCase() === "awarded";
                  const isPushed = !!li.pushed_to_project;
                  return (
                    <TableRow key={li.id || idx} data-testid={`award-row-${idx}`}>
                      <TableCell>
                        <Checkbox
                          checked={isAwarded}
                          disabled={saving}
                          onCheckedChange={() => (isAwarded ? toggleLine(li.id, "pending") : askDate({ lineItemId: li.id, label: li.description }))}
                          data-testid={`award-checkbox-${idx}`}
                          title={isAwarded && isPushed ? "Un-awarding will show as an Award update pending on the project" : ""}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{li.description || `Item ${idx + 1}`}</TableCell>
                      <TableCell className="text-right">{li.estimated_detailing_hours || 0}h</TableCell>
                      <TableCell className="text-right">{fmtCurrency(li.subtotal || 0)}</TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {isAwarded ? (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => askDate({ lineItemId: li.id, label: li.description, current: (li.awarded_date || "").slice(0, 10), edit: true })}
                            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-yellow-50 hover:text-yellow-800 transition-colors"
                            title="Change award date"
                            data-testid={`award-date-edit-${idx}`}
                          >
                            {fmtDate(li.awarded_date)} <Pencil className="h-3 w-3" />
                          </button>
                        ) : fmtDate(li.awarded_date)}
                      </TableCell>
                      <TableCell>
                        {isPushed ? (
                          <Badge className="bg-blue-600 text-white">Pushed to Project</Badge>
                        ) : isAwarded ? (
                          <Badge className="bg-green-600 text-white">Awarded</Badge>
                        ) : (
                          <Badge variant="secondary">Pending</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <div className="border-t pt-3 text-sm text-gray-700 flex gap-6">
              <div>Top-level status: <Badge className="ml-1">{estimation?.award_status || "pending"}</Badge></div>
              <div>Awarded total: <b>{estimation?.awarded_hours || 0}h</b> · <b>{fmtCurrency(estimation?.awarded_value || 0)}</b></div>
            </div>
          </div>
        )}
        <Dialog open={!!datePrompt} onOpenChange={(o) => !o && setDatePrompt(null)}>
          <DialogContent className="max-w-sm bg-white" data-testid="award-date-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Award date</DialogTitle>
              <p className="text-sm text-gray-600">
                {datePrompt?.all ? `All ${stats.pendingCount} pending line items` : datePrompt?.label || "Line item"} — {datePrompt?.edit ? "change the award date" : "when was this awarded?"}
              </p>
            </DialogHeader>
            <DatePicker value={awardDate} max={localToday()} onChange={(e) => setAwardDate(e.target.value)} data-testid="award-date-input" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setDatePrompt(null)} data-testid="award-date-cancel">Cancel</Button>
              <Button onClick={confirmDate} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-testid="award-date-confirm">{datePrompt?.edit ? "Save Date" : "Award"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
};

export default ManageAwardsSheet;
